import React from 'react';
import aboutImage from '../../../assets/images/inner_design_heading.svg';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

const AppPrivacyPolicy: React.FC = () => {
  useDocumentTitle('Namasteji Manikchand - App Privacy Policy');
  return (
    <section className='w-full bg-white text-[#333] font-sans'>
      {/* Section Header */}
      <section
        className='py-3 sm:py-3 md:py-12 lg:py-12 px-4 sm:px-8 md:px-16 text-center bg-white'
        style={{
          borderBottom: '1px solid',
          borderTop: '1px solid',
          borderImageSource:
            'linear-gradient(90deg, #C79856 6.06%, #FAD983 27.27%, #DFB662 47.27%, #FAD983 72.53%, #B27F32 95.6%)',
          borderImageSlice: 1,
        }}
      >
        <h2 className='font-rubik text-[28px] sm:text-[28px] md:text-[32px] lg:text-[32px] font-bold text-[#4A2D1A]'>
          APP PRIVACY POLICY{' '}
        </h2>

        {/* Decorative Image */}
        <div className='flex justify-center my-1 sm:my-1 md:my-6 lg:my-6'>
          <img
            src={aboutImage}
            alt='Decorative Line'
            className='max-w-[70%] sm:max-w-[50%] md:max-w-[50%] lg:max-w-[50%] h-auto'
          />
        </div>

        {/* Content Section */}
        <div className='max-w-6xl mx-auto text-sm sm:text-base md:text-base text-left text-gray-800 space-y-2 sm:space-y-2 md:space-y-6 lg:space-y-6 leading-relaxed'>
          <div className='text-justify'>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold py-0 sm:py-0 md:py-2 lg:py-2'>
              INTRODUCTION
            </h4>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              Welcome to Namasteji Manikchand. Your privacy is important to us. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you use our mobile application or website.
            </p>
          </div>
          {/* INFORMATION WE COLLECT */}
          <div>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl py-0 sm:py-0 md:py-2 lg:py-2 mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold '>
              INFORMATION WE COLLECT
            </h4>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              We may collect the following types of information:
            </p>
            <h6 className='font-rubik text-xl sm:text-base md:text-xl lg:text-xl py-1 sm:py-1 md:py-2 lg:py-2 max-w-6xl mx-auto leading-relaxed text-start font-bold text-black'>
              A. Personal Information:
            </h6>
            <ol className='list-decimal pl-5 space-y-1 sm:space-y-1 md:space-y-3 lg:space-y-3 font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              <li className='text-justify'>Full name</li>
              <li className='text-justify'>Email address</li>
              <li className='text-justify'>Contact number</li>
              <li className='text-justify'>Contact number Location (only if necessary and permitted)</li>
            </ol>

            <h6 className='font-rubik text-xl sm:text-base md:text-xl lg:text-xl py-1 sm:py-1 md:py-2 lg:py-2 pt-2 sm:pt-2 md:pt-4 lg:pt-4 max-w-6xl mx-auto leading-relaxed text-start font-bold text-black'>
              B. Usage Data:
            </h6>
            <ol className='list-decimal pl-5 space-y-1 sm:space-y-1 md:space-y-3 lg:space-y-3 font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              <li className='text-justify'>Device information (model, OS, IP address)</li>
              <li className='text-justify'>App interactions (screens viewed)</li>
              <li className='text-justify'>Crash logs and diagnostics</li>
            </ol>
            <h6 className='font-rubik text-xl sm:text-base md:text-xl lg:text-xl py-1 sm:py-1 md:py-2 lg:py-2 pt-2 sm:pt-2 md:pt-4 lg:pt-4 max-w-6xl mx-auto leading-relaxed text-start font-bold text-black'>
              C. Location Information:
            </h6>
            <ol className='list-decimal pl-5 space-y-1 sm:space-y-1 md:space-y-3 lg:space-y-3 font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              <li className='text-justify'>
                Collected only if required for app functionality (e.g., delivery, navigation)
              </li>
              <li className='text-justify'>You may choose to allow or deny access</li>
              <li className='text-justify'>To track and manage deliveries.</li>
              <li className='text-justify'>To monitor and assist sales personnel during field operations.</li>
              <li className='text-justify'>
                To capture the user’s location at the time of application/registration for verification purposes.
              </li>
              <p className='italic text-gray-600 text-sm text-justify'>
                <strong>Note:</strong> Location data is only used for these business and functional purposes. It is not
                sold or shared with unauthorized third parties. Users may control location permissions via their device
                settings, but disabling location may limit the functionality of the app.
              </p>
            </ol>
          </div>

          {/* HOW WE USE YOUR INFORMATION */}
          <div>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl py-0 sm:py-0 md:py-2 lg:py-2 pt-0 sm:pt-0 md:pt-4 lg:pt-4 mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold '>
              HOW WE USE YOUR INFORMATION
            </h4>
            <ol className='list-decimal pl-5 space-y-1 sm:space-y-1 md:space-y-3 lg:space-y-3 font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              <li className='text-justify'>Provide and improve our services</li>
              <li className='text-justify'>Personalize your experience</li>
              <li className='text-justify'>Communicate updates, support, and promotional content</li>
              <li className='text-justify'>Monitor app performance and troubleshoot issues</li>
              <li className='text-justify'>Comply with legal obligations</li>
            </ol>
          </div>

          {/* SHARING YOUR INFORMATION */}
          <div>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl py-0 sm:py-0 md:py-2 lg:py-2 mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold '>
              SHARING YOUR INFORMATION
            </h4>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              We do not sell or rent your personal data. We may share your data with:
            </p>
            <ol className='list-decimal pl-5 mt-0 sm:mt-0 md:mt-2 lg:mt-2 space-y-1 sm:space-y-1 md:space-y-3 lg:space-y-3 font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              <li className='text-justify'>Service Providers (e.g., hosting, analytics, payment processing)</li>
              <li className='text-justify'>Personalize your experience</li>
              <li className='text-justify'>Legal Authorities if required by law</li>
              <li className='text-justify'>Business Transfers in case of merger or acquisition</li>
            </ol>
          </div>

          {/* DATA SECURITY */}
          <div className='text-justify'>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold py-0 sm:py-0 md:py-2 lg:py-2'>
              DATA SECURITY
            </h4>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              We implement technical and organizational measures to protect your data. However, no method of
              transmission over the Internet is 100% secure.
            </p>
          </div>

          {/* YOUR RIGHTS */}
          <div>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl py-0 sm:py-0 md:py-2 lg:py-2 mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold '>
              YOUR RIGHTS
            </h4>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              You have the right to:
            </p>
            <ol className='list-decimal pl-5 mt-0 sm:mt-0 md:mt-2 lg:mt-2 space-y-1 sm:space-y-1 md:space-y-3 lg:space-y-3 font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify mb-2 sm:mb-2 md:mb-5 lg:mb-5'>
              <li className='text-justify'>Access, correct, or delete your data</li>
              <li className='text-justify'>Withdraw consent (e.g., for location tracking)</li>
              <li className='text-justify'>File a complaint with a data protection authority</li>
            </ol>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              To request action, contact us at <strong>[your email/contact form]</strong>.
            </p>
          </div>

          {/* CHILDREN’S PRIVACY */}
          <div className='text-justify'>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold py-0 sm:py-0 md:py-2 lg:py-2'>
              CHILDREN’S PRIVACY
            </h4>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              We do not knowingly collect data from children under 13. If you believe a child has provided personal
              data, please contact us to delete it.
            </p>
          </div>

          {/* CHANGES TO THIS PRIVACY POLICY */}
          <div className='text-justify'>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold py-0 sm:py-0 md:py-2 lg:py-2'>
              CHANGES TO THIS PRIVACY POLICY
            </h4>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              We may update this policy from time to time. Changes will be posted on this page with a new Last Updated
              date.
            </p>
          </div>

          <div>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl py-0 sm:py-0 md:py-2 lg:py-2 mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold '>
              WHY WE REQUEST LOCATION & GPS ACCESS
            </h4>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              Our app requests access to your device’s location (both foreground and background) strictly for attendance
              and work-related tracking purposes. Heres how and why its used:
            </p>
            <ol className='list-decimal pl-5 mt-0 sm:mt-0 md:mt-2 lg:mt-2 space-y-1 sm:space-y-1 md:space-y-3 lg:space-y-3 font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              <li className='text-justify'>
                When you check in for work, location tracking begins to accurately record your location and confirm your
                attendance.
              </li>
              <li className='text-justify'>
                Your live location is securely transmitted to our server and shared only with your organization to
                validate your check-in location.
              </li>
              <li className='text-justify'>
                We also store the geocoded address to generate accurate reports and support work verification
              </li>
              <li className='text-justify'>Location tracking automatically stops once you check out.</li>
              <li className='text-justify'>
                We do not use your location data for any other purposes, and we never share it with third parties.
              </li>
            </ol>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              We are fully committed to respecting your privacy and ensuring that your data is used responsibly and only
              as needed for essential functionality.
            </p>
          </div>

          {/* CONTACT US */}
          <div className='text-justify'>
            <h4 className='font-rubik text-[22px] sm:text-[22px] md:text-[30px] lg:text-[30px] max-w-6xl mx-auto text-[#2B2B2B] leading-relaxed text-start font-bold py-0 sm:py-0 md:py-2 lg:py-2'>
              CONTACT US
            </h4>
            <p className='font-poppins text-black text-sm sm:text-sm md:text-lg lg:text-lg max-w-6xl mx-auto leading-relaxed text-justify'>
              If you have questions about this Privacy Policy, contact us:{' '}
              <strong>Email: info@globalmanikchand.in</strong>
            </p>
          </div>
        </div>
      </section>
    </section>
  );
};

export default AppPrivacyPolicy;
